// Helper utility functions
// Based on documentation from docs/

import { IMAGE_TYPE, VIDEO_TYPE, AUDIO_TYPE, FILE_TYPE, USER_COLORS } from './constants';

/**
 * Get current time in formatted string
 * @returns {string} Formatted time string (e.g., "4/1/2026<br />02:30 PM")
 */
export function getCurrentTime() {
  const now = new Date();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = now.getDate();
  const month = months[now.getMonth()];
  const year = String(now.getFullYear()).slice(-2);
  
  let hours = now.getHours();
  const minutes = now.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = (hours % 12 || 12).toString().padStart(2, '0');
  
  return `${day} ${month}, ${year} | ${hours}:${minutes} ${ampm}`;
}

/**
 * Convert string to title case
 * @param {string} str Input string
 * @returns {string} Title cased string
 */
export function toTitleCase(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Extract initials from name
 * @param {string} name User name
 * @returns {string} Initials (e.g., "JD" from "John Doe")
 */
export function initialsFrom(name) {
  if (!name || name.trim() === '') return '--';
  
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  // For single word, return first char + first digit if exists, otherwise first 2 chars
  const match = name.match(/[A-Za-z].*?(\d+)/);
  if (match) {
    return (name[0] + match[1]).toUpperCase();
  }
  return (name.substring(0, 2)).toUpperCase();
}

/**
 * Get file extension from filename
 * @param {string} filename File name
 * @returns {string} File extension in lowercase
 */
export function getFileExt(filename) {
  if (!filename) return '';
  return filename.split('.').pop().toLowerCase();
}

/**
 * Get appropriate image source for file type
 * @param {string} fileName File name
 * @param {string} imageSrc Actual image source
 * @returns {string} Image source or icon path
 */
export function getImageSrc(fileName, imageSrc) {
  const ext = getFileExt(fileName);
  
  // For document files, use icon
  if (FILE_TYPE.includes(ext)) {
    return `/images/logo/${ext}.png`;
  }
  
  // For media files, use actual image
  return imageSrc || '/images/placeholder.png';
}

/**
 * Check if string is an emoji
 * @param {string} character Character to check
 * @returns {boolean} True if emoji
 */
export function isEmoji(character) {
  if (!character) return false;
  const emojiRegex = /(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}(?:\uFE0F|\uD83C[\uDFFB-\uDFFF])?)/gu;
  return emojiRegex.test(character);
}

/**
 * Detect if message contains a link
 * @param {string} text Message text
 * @returns {object} {is_link: boolean, link: string}
 */
export function haveLink(text) {
  const urlRegex = /(https?:\/\/[^\s]+)/;
  const match = text.match(urlRegex);
  
  if (match) {
    return { is_link: true, link: match[0] };
  }
  return { is_link: false, link: '' };
}

/**
 * Generate random string
 * @param {number} length String length
 * @returns {string} Random string
 */
export function generateRandomString(length = 7) {
  return Math.random().toString(36).substr(2, length);
}

/**
 * Normalize filename for comparison
 * @param {string} name File name
 * @returns {string} Normalized name
 */
export function normalizeName(name) {
  if (!name) return '';
  return name.toLowerCase().trim();
}

/**
 * Adjust filename to ensure consistent length
 * @param {File} file Original file
 * @returns {File} File with adjusted name
 */
export function adjustFileName(file) {
  const originalName = file.name;
  const fileExtension = originalName.substring(originalName.lastIndexOf('.'));
  const baseName = originalName.substring(0, originalName.lastIndexOf('.'));
  const maxBaseLength = 16 - fileExtension.length;
  
  // Truncate or pad to max length
  let adjustedBaseName;
  if (baseName.length > maxBaseLength) {
    adjustedBaseName = baseName.substring(0, maxBaseLength);
  } else {
    adjustedBaseName = baseName.padEnd(maxBaseLength, generateRandomString(maxBaseLength - baseName.length));
  }
  
  const adjustedFileName = adjustedBaseName + fileExtension;
  
  return new File([file], adjustedFileName, { type: file.type });
}

/**
 * Download blob URL as file
 * @param {string} blobUrl Blob URL
 * @param {string} filename File name
 */
export function downloadBlobUrl(blobUrl, filename) {
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Get random color from palette
 * @param {number} index Index in palette
 * @returns {string} Color code
 */
export function getRandomColor(index = null) {
  if (index !== null && index < USER_COLORS.length) {
    return USER_COLORS[index];
  }
  return USER_COLORS[Math.floor(Math.random() * USER_COLORS.length)];
}

/**
 * Debounce function
 * @param {Function} func Function to debounce
 * @param {number} wait Wait time in ms
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function
 * @param {Function} func Function to throttle
 * @param {number} limit Limit time in ms
 * @returns {Function} Throttled function
 */
export function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Safe send message via connection
 * @param {object} conn Connection object with text channel
 * @param {object} message Message to send
 * @returns {boolean} Success status
 */
export function safeSend(conn, message) {
  try {
    if (conn && conn.text && conn.text.open) {
      conn.text.send(message);
      return true;
    }
  } catch (e) {
    console.warn('SafeSend failed:', e);
  }
  return false;
}

/**
 * Create message object
 * @param {string} type Message type
 * @param {object} currentUser Current user info
 * @param {string} userBlock User block HTML
 * @param {object} data Additional data
 * @returns {object} Message object
 */
export function msgObj(type, currentUser, userBlock, data) {
  return {
    type,
    currentUser,
    userBlock,
    data,
    timestamp: Date.now()
  };
}

/**
 * Compute initials from peer ID (fallback)
 * @param {string} peerId Peer ID
 * @returns {string} Initials
 */
export function computeInitials(peerId) {
  if (!peerId) return '??';
  const clean = String(peerId).replace(/[^a-zA-Z0-9]/g, '');
  if (clean.length >= 2) return clean.substring(0, 2).toUpperCase();
  return (clean + 'X').substring(0, 2).toUpperCase();
}

/**
 * Check if file type is supported
 * @param {string} fileName File name
 * @returns {boolean} True if supported
 */
export function isFileTypeSupported(fileName) {
  const ext = getFileExt(fileName);
  return [...IMAGE_TYPE, ...VIDEO_TYPE, ...AUDIO_TYPE, ...FILE_TYPE].includes(ext);
}

/**
 * Format file size
 * @param {number} bytes File size in bytes
 * @returns {string} Formatted size (e.g., "2.3 MB")
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Play sound notification
 * @param {string} soundId Sound element ID
 */
export function playSound(soundId) {
  try {
    const audio = document.getElementById(soundId);
    if (audio) {
      audio.play().catch(e => console.warn('Audio play failed:', e));
    }
  } catch (e) {
    console.warn('playSound error:', e);
  }
}

/**
 * Copy text to clipboard
 * @param {string} text Text to copy
 * @returns {Promise<boolean>} Success status
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      return success;
    }
  } catch (e) {
    console.error('Copy to clipboard failed:', e);
    return false;
  }
}

/**
 * Format duration in seconds to MM:SS
 * @param {number} seconds Duration in seconds
 * @returns {string} Formatted duration
 */
export function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Detect if user is on mobile device
 * @returns {boolean} True if mobile
 */
export function isMobile() {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Get browser name
 * @returns {string} Browser name
 */
export function getBrowserName() {
  if (typeof window === 'undefined') return 'Unknown';
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
  if (userAgent.indexOf('Chrome') > -1) return 'Chrome';
  if (userAgent.indexOf('Safari') > -1) return 'Safari';
  if (userAgent.indexOf('Edge') > -1) return 'Edge';
  return 'Unknown';
}
